import dashboard from "@/api/dashboard"
import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import { GetMenuResult, Menu, MenuItem } from "@/api/shopify-graphql/shopify-graphql.type"
import { DashboardSetupGuideEnum } from "@/enums/dashboard"
import { appProxySubpathPrefixList } from "@/utils/constant"

export interface MenuTrackingPageCheckResult {
  itemsCount: number
  menuData: GetMenuResult | null
  menusToUpdate: Array<{
    menu: Menu
    updatedItems: MenuItem[]
  }>
}

interface CheckTrackingPageResult extends MenuTrackingPageCheckResult {
  isCompleted: boolean
}

const normalizeMenuItemUrl = (url: string | null | undefined): string => {
  if (!url) {
    return ""
  }

  const trimmedUrl = url.trim()

  if (!trimmedUrl) {
    return ""
  }

  try {
    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      const pathname = new URL(trimmedUrl).pathname.replace(/\/$/, "") || "/"

      return pathname
    }
  } catch {
    return trimmedUrl.replace(/\/$/, "")
  }

  return trimmedUrl.startsWith("/")
    ? trimmedUrl.replace(/\/$/, "")
    : `/${trimmedUrl}`.replace(/\/$/, "")
}

const isMenuUrlMatched = (itemUrl: string | null | undefined, targetURL: string): boolean => {
  const normalizedTarget = normalizeMenuItemUrl(targetURL)

  if (!normalizedTarget) {
    return false
  }

  return normalizeMenuItemUrl(itemUrl) === normalizedTarget
}

const getMenuTrackingPageCheckResult = (
  menus: Menu[],
  searchUrl: string,
  replaceUrl?: string,
): MenuTrackingPageCheckResult => {
  let matchingItemsCount = 0
  const menusToUpdate: MenuTrackingPageCheckResult["menusToUpdate"] = []

  menus.forEach(menu => {
    if (!menu.items?.length) {
      return
    }

    const menuMatchingCount = countMatchingMenuItems(menu.items, searchUrl)

    matchingItemsCount += menuMatchingCount

    if (replaceUrl && menuMatchingCount > 0) {
      menusToUpdate.push({
        menu,
        updatedItems: updateMenuItemsURL(menu.items, searchUrl, replaceUrl),
      })
    }
  })

  return {
    itemsCount: matchingItemsCount,
    menuData: null,
    menusToUpdate,
  }
}

export function getShortTrackLink(url: string) {
  if (!url) return ""
  // 确保 URL 是完整的
  const fullUrl = url.startsWith("http") ? url : `https://${url}`

  const urlParts = new URL(fullUrl)
  const { pathname } = urlParts

  for (const prefix of appProxySubpathPrefixList) {
    if (pathname.startsWith(`/${prefix}`)) {
      const splitPath = pathname.split("/").slice(2) // 去掉前缀部分

      return `/${prefix}/${splitPath}`
    }
  }

  return pathname // 如果没有匹配的前缀，返回原路径
}

/**
 * 递归计算匹配URL的菜单项总数（包括子菜单，最多3级）
 * @param items 菜单项数组
 * @param targetURL 目标URL
 * @param currentLevel 当前层级（从1开始）
 * @returns 匹配URL的菜单项总数
 */
const countMatchingMenuItems = (items: MenuItem[], targetURL: string, currentLevel: number = 1): number => {
  let count = 0

  items.forEach(item => {
    // 检查当前菜单项的URL是否匹配
    if (isMenuUrlMatched(item.url, targetURL)) {
      count++
    }

    // 最多处理3级菜单，递归检查子菜单
    if (currentLevel < 3 && item.items && item.items.length > 0) {
      count += countMatchingMenuItems(item.items, targetURL, currentLevel + 1)
    }
  })

  return count
}

/**
 * 递归更新菜单项中的URL（包括子菜单，最多3级）
 * @param items 菜单项数组
 * @param oldURL 旧URL
 * @param newURL 新URL
 * @param currentLevel 当前层级（从1开始）
 * @returns 更新后的菜单项数组
 */
const updateMenuItemsURL = (items: MenuItem[], oldURL: string, newURL: string, currentLevel: number = 1): MenuItem[] => {
  return items.map(item => {
    const updatedItem = { ...item }

    // 检查当前菜单项的URL是否匹配并更新
    if (isMenuUrlMatched(item.url, oldURL)) {
      updatedItem.url = newURL
    }

    // 最多处理3级菜单，递归更新子菜单
    if (currentLevel < 3 && item.items && item.items.length > 0) {
      updatedItem.items = updateMenuItemsURL(item.items, oldURL, newURL, currentLevel + 1)
    }

    return updatedItem
  })
}

export const checkMenuItemsByUrl = async (
  searchUrl: string,
  replaceUrl?: string,
): Promise<MenuTrackingPageCheckResult> => {
  const menuResponse = await ShopifyGraphql.getMenu()

  if (menuResponse.errors?.[0]) {
    throw new Error(menuResponse.errors[0].message)
  }

  const menus = menuResponse.data?.menus?.nodes?.filter(
    menu => menu.handle !== "customer-account-main-menu",
  ) ?? []
  const result = getMenuTrackingPageCheckResult(menus, searchUrl, replaceUrl)

  return {
    ...result,
    menuData: menuResponse.data || null,
  }
}

/**
 * 检查 ADD_TRACKING_PAGE 任务是否完成并验证菜单项
 * @param itemURL 菜单项URL
 * @param newURL 新的URL（可选，用于更新）
 * @returns Promise<CheckTrackingPageResult> 包含完成状态、菜单数据和需要更新的菜单
 */
export const checkAddTrackingPage = async (itemURL: string, newURL?: string): Promise<CheckTrackingPageResult> => {
  try {
    const [setupGuideResponse, menuCheckResult] = await Promise.all([
      dashboard.getSetupGuide(),
      checkMenuItemsByUrl(itemURL, newURL),
    ])

    const { data } = setupGuideResponse
    const targetTask = data.setup_guide?.task?.find(task => task.id === DashboardSetupGuideEnum.ADD_TRACKING_PAGE)
    const isTaskCompleted = targetTask?.is_completed || false

    return {
      isCompleted: isTaskCompleted,
      ...menuCheckResult,
    }
  } catch (error) {
    console.error("获取设置引导或菜单数据失败:", error)
    return {
      isCompleted: false,
      itemsCount: 0,
      menuData: null,
      menusToUpdate: [],
    }
  }
}
